import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, fmtMXN } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MapPreview from "@/components/MapPreview";
import { getCurrentLocation } from "@/lib/geolocation";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTheme } from "@/context/ThemeContext";
import { Flame, Plus, Minus, Trash2, ShoppingCart, Moon, Sun, MapPin, CheckCircle2, Pizza, AlertTriangle, Banknote, CreditCard, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PedidoPublico() {
  const { sucursalId } = useParams();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();

  // Estado de carga / validación de sucursal de URL
  const [bootstrapping, setBootstrapping] = useState(true);
  const [branchError, setBranchError] = useState(null);

  // Sucursales hermanas (mismo cliente). Selector se muestra solo si hay >1.
  const [sucursales, setSucursales] = useState([]);
  const [selectedSuc, setSelectedSuc] = useState(sucursalId || "");
  const [menu, setMenu] = useState(null);

  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState("ALL");
  const itemIdRef = React.useRef(0);
  const nextItemId = () => `it-${++itemIdRef.current}`;

  const [variantOpen, setVariantOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currentVariantes, setCurrentVariantes] = useState([]);
  const [currentCantidad, setCurrentCantidad] = useState(1);
  const [currentObs, setCurrentObs] = useState("");
  const [incluirAcomp, setIncluirAcomp] = useState(true);

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [datos, setDatos] = useState({
    cliente_nombre: "",
    cliente_telefono: "",
    entrega_tipo: "RECOGE",
    cliente_direccion: "",
    referencias: "",
    direccion_origen: "MANUAL",
    metodo_pago: "EFECTIVO",
    monto_con_que_paga: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmacion, setConfirmacion] = useState(null);

  // Bootstrap: validar la sucursal de la URL y traer hermanas del mismo cliente
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setBootstrapping(true);
      setBranchError(null);
      try {
        if (sucursalId) {
          // 1. Validar la sucursal de la URL (controlado: 404 => error claro)
          let menuResp;
          try {
            menuResp = await api.get(`/public/menu?sucursal_id=${sucursalId}`);
          } catch (e) {
            if (cancelled) return;
            setBranchError("La sucursal indicada no existe o no está disponible.");
            setMenu(null);
            setBootstrapping(false);
            return;
          }
          if (cancelled) return;
          setMenu(menuResp.data);
          setSelectedSuc(sucursalId);
          // 2. Traer hermanas del mismo cliente (para decidir si mostrar selector)
          const sibs = await api.get(`/public/sucursales?sucursal_id=${sucursalId}`);
          if (cancelled) return;
          setSucursales(sibs.data || []);
        } else {
          // Sin sucursal en URL: lista pública legacy (mostrar selector)
          const r = await api.get("/public/sucursales");
          if (cancelled) return;
          setSucursales(r.data || []);
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [sucursalId]);

  // Si el usuario cambia de sucursal usando el selector, actualizar la URL
  const onChangeSucursal = (newId) => {
    if (!newId || newId === selectedSuc) return;
    setItems([]); // Resetear carrito al cambiar de sucursal
    setMenu(null);
    setSelectedSuc(newId);
    navigate(`/pedido/${newId}`, { replace: false });
  };

  // Cargar menú cuando cambie la sucursal seleccionada (sin URL inicial)
  useEffect(() => {
    if (!selectedSuc) return;
    // Si ya se cargó durante bootstrap, no recargar
    if (menu && menu.sucursal?.id === selectedSuc) return;
    api.get(`/public/menu?sucursal_id=${selectedSuc}`)
      .then((r) => setMenu(r.data))
      .catch(() => setMenu(null));
  }, [selectedSuc, menu]);

  const filtered = useMemo(() => {
    if (!menu) return [];
    if (activeCat === "ALL") return menu.productos;
    return menu.productos.filter((p) => p.categoria_id === activeCat);
  }, [menu, activeCat]);

  const addProducto = (prod) => {
    const tieneVariantes = (prod.variantes || []).length > 0;
    const tieneAcomp = !!prod.acompanamiento_id;
    if (tieneVariantes || tieneAcomp) {
      setCurrentProduct(prod);
      const pre = [];
      (prod.variantes || []).forEach((g) => {
        if (g.obligatoria && !g.multiple && g.opciones[0]) pre.push({ grupo: g.nombre, opcion: g.opciones[0].nombre, precio_extra: g.opciones[0].precio_extra });
      });
      setCurrentVariantes(pre);
      setCurrentCantidad(1); setCurrentObs("");
      setIncluirAcomp(true);
      setVariantOpen(true);
    } else {
      setItems([...items, { id: nextItemId(), producto_id: prod.id, nombre: prod.nombre, precio_base: prod.precio_base, cantidad: 1, variantes: [], observaciones: "", acompanamiento_nombre: prod.acompanamiento_nombre || null }]);
    }
  };

  const setOpcion = (grupo, opcion) => {
    if (grupo.multiple) {
      const exists = currentVariantes.find((v) => v.grupo === grupo.nombre && v.opcion === opcion.nombre);
      if (exists) setCurrentVariantes(currentVariantes.filter((v) => !(v.grupo === grupo.nombre && v.opcion === opcion.nombre)));
      else setCurrentVariantes([...currentVariantes, { grupo: grupo.nombre, opcion: opcion.nombre, precio_extra: opcion.precio_extra }]);
    } else {
      const filtered = currentVariantes.filter((v) => v.grupo !== grupo.nombre);
      setCurrentVariantes([...filtered, { grupo: grupo.nombre, opcion: opcion.nombre, precio_extra: opcion.precio_extra }]);
    }
  };

  const confirmVariantes = () => {
    const missing = (currentProduct.variantes || []).find((g) => g.obligatoria && !currentVariantes.some((v) => v.grupo === g.nombre));
    if (missing) return toast.error(`Falta seleccionar: ${missing.nombre}`);
    const tieneAcomp = !!currentProduct.acompanamiento_id;
    setItems([...items, {
      id: nextItemId(),
      producto_id: currentProduct.id,
      nombre: currentProduct.nombre,
      precio_base: currentProduct.precio_base,
      cantidad: currentCantidad,
      variantes: currentVariantes,
      observaciones: currentObs,
      acompanamiento_id: tieneAcomp ? currentProduct.acompanamiento_id : null,
      acompanamiento_nombre: tieneAcomp ? currentProduct.acompanamiento_nombre : null,
      acompanamiento_tiene_costo: tieneAcomp ? !!currentProduct.acompanamiento_tiene_costo : false,
      acompanamiento_costo: tieneAcomp ? (currentProduct.acompanamiento_costo || 0) : 0,
      incluir_acompanamiento: tieneAcomp ? !!incluirAcomp : null,
    }]);
    setVariantOpen(false);
  };

  const subtotalItem = (it) => {
    const base = (it.precio_base + (it.variantes || []).reduce((s, v) => s + (v.precio_extra || 0), 0)) * it.cantidad;
    const acomp = (it.incluir_acompanamiento && it.acompanamiento_tiene_costo)
      ? (it.acompanamiento_costo || 0) * it.cantidad
      : 0;
    return base + acomp;
  };
  const total = items.reduce((s, it) => s + subtotalItem(it), 0);

  const usarGps = async () => {
    try {
      const { lat, lng } = await getCurrentLocation();
      setDatos((d) => ({
        ...d,
        latitud: lat,
        longitud: lng,
        direccion_origen: "GPS",
        ubicacion_capturada_en: new Date().toISOString(),
      }));
      toast.success("Ubicación capturada");
    } catch (err) {
      toast.error(err?.message || "No se pudo obtener la ubicación actual.");
    }
  };

  // Permite al cliente ajustar el marcador en el mapa.
  const onMoveMarker = ({ lat, lng }) => {
    setDatos((d) => ({
      ...d,
      latitud: lat,
      longitud: lng,
      direccion_origen: "GPS",
      ubicacion_capturada_en: new Date().toISOString(),
    }));
  };

  // ¿Debe pedirse "con cuánto paga" para calcular cambio?
  const requiereMontoConCualPaga = datos.metodo_pago === "EFECTIVO" && datos.entrega_tipo === "DOMICILIO";
  const montoPaga = parseFloat(datos.monto_con_que_paga || "0");
  const cambioCalculado = requiereMontoConCualPaga && montoPaga >= total ? Math.round((montoPaga - total) * 100) / 100 : null;

  const enviar = async () => {
    if (!datos.cliente_nombre || !datos.cliente_telefono) return toast.error("Nombre y teléfono son obligatorios");
    if (datos.entrega_tipo === "DOMICILIO" && !datos.cliente_direccion && !datos.latitud) return toast.error("Captura dirección o comparte ubicación");
    if (items.length === 0) return toast.error("Agrega productos");
    if (!datos.metodo_pago) return toast.error("Selecciona un método de pago");
    if (requiereMontoConCualPaga) {
      if (!datos.monto_con_que_paga || Number.isNaN(montoPaga)) return toast.error("Indica con cuánto vas a pagar");
      if (montoPaga < total) return toast.error(`El monto debe ser mayor o igual al total (${fmtMXN(total)})`);
    }
    setSubmitting(true);
    try {
      const payload = {
        sucursal_id: selectedSuc,
        cliente_nombre: datos.cliente_nombre,
        cliente_telefono: datos.cliente_telefono,
        entrega_tipo: datos.entrega_tipo,
        cliente_direccion: datos.cliente_direccion,
        referencias: datos.referencias,
        direccion_origen: datos.direccion_origen,
        latitud: datos.latitud,
        longitud: datos.longitud,
        ubicacion_capturada_en: datos.ubicacion_capturada_en,
        items: items.map((it) => ({ producto_id: it.producto_id, cantidad: it.cantidad, variantes: it.variantes, observaciones: it.observaciones, incluir_acompanamiento: it.acompanamiento_id ? !!it.incluir_acompanamiento : null })),
        metodo_pago_solicitado: datos.metodo_pago,
        monto_con_que_paga: requiereMontoConCualPaga ? montoPaga : null,
      };
      const r = await api.post("/public/ordenes", payload);
      setConfirmacion({ ...r.data, metodo_pago: datos.metodo_pago, cambio: cambioCalculado });
      setItems([]);
      setCheckoutOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const hacerOtroPedido = () => {
    // Reset estado del flujo de pedido pero mantenemos la sucursal y la URL
    setConfirmacion(null);
    setDatos({
      cliente_nombre: "",
      cliente_telefono: "",
      entrega_tipo: "RECOGE",
      cliente_direccion: "",
      referencias: "",
      direccion_origen: "MANUAL",
      metodo_pago: "EFECTIVO",
      monto_con_que_paga: "",
    });
    setItems([]);
    setActiveCat("ALL");
    // IMPORTANT: no reseteamos selectedSuc — el usuario permanece en /pedido/{sucursalId}
  };

  // ============== RENDER ==============

  // Cargando bootstrap
  if (bootstrapping) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <Flame className="h-10 w-10 text-primary mx-auto animate-pulse" />
          <div className="text-sm text-muted-foreground uppercase tracking-widest">Cargando…</div>
        </div>
      </div>
    );
  }

  // Error: branchPublicId inválido
  if (branchError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-md" data-testid="branch-error-card">
          <CardContent className="p-8 text-center space-y-4">
            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto" />
            <h2 className="text-2xl font-black">Sucursal no disponible</h2>
            <p className="text-sm text-muted-foreground">{branchError}</p>
            <p className="text-xs text-muted-foreground">Verifica el enlace que recibiste o contacta a la sucursal.</p>
            <Button variant="outline" className="w-full" onClick={() => navigate("/pedido")} data-testid="branch-error-back-btn">
              Ver sucursales disponibles
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Sin sucursal en URL y aún no seleccionada → mostrar selector legacy
  if (!selectedSuc) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 space-y-4">
            <div className="flex items-center gap-3">
              <img src="/mkitchen-logo.png" alt="M-Kitchen" className="h-12 w-12 rounded-xl bg-black p-1 object-contain" />
              <div>
                <div className="text-xl font-black">M-Kitchen</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Pedido en línea</div>
              </div>
            </div>
            <h2 className="text-2xl font-black">Selecciona tu sucursal</h2>
            <Select value={selectedSuc} onValueChange={onChangeSucursal}>
              <SelectTrigger className="h-12" data-testid="public-sucursal-select"><SelectValue placeholder="Sucursal" /></SelectTrigger>
              <SelectContent>{sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}</SelectContent>
            </Select>
            {sucursales.length === 0 && <div className="text-sm text-muted-foreground">No hay sucursales recibiendo pedidos en este momento.</div>}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (confirmacion) {
    const trackUrl = `/seguimiento/${encodeURIComponent(confirmacion.numero)}?tel=${encodeURIComponent(datos.cliente_telefono)}`;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
            <h2 className="text-3xl font-black">¡Pedido recibido!</h2>
            <p className="text-sm text-muted-foreground">{confirmacion.mensaje}</p>
            <div className="bg-secondary rounded-lg p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Número de orden</div>
              <div className="text-3xl font-black text-primary">{confirmacion.numero}</div>
            </div>
            {confirmacion.metodo_pago && (
              <div className="rounded-lg border border-border p-3 text-left text-sm space-y-1" data-testid="confirm-payment-summary">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Pago</div>
                <div>Método: <span className="font-bold">{confirmacion.metodo_pago}</span></div>
                {confirmacion.cambio !== null && confirmacion.cambio !== undefined && (
                  <div>El repartidor te llevará cambio de <span className="font-bold text-primary">{fmtMXN(confirmacion.cambio)}</span></div>
                )}
              </div>
            )}
            <a href={trackUrl} className="block w-full" data-testid="seguimiento-link">
              <Button variant="outline" className="w-full h-11 font-bold">Ver estado de mi pedido</Button>
            </a>
            <Button className="w-full h-12" onClick={hacerOtroPedido} data-testid="nuevo-pedido-btn">Hacer otro pedido</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Mostrar selector inline solo si hay >1 sucursal hermana
  const mostrarSelectorHermanas = sucursales.length > 1;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/mkitchen-logo.png" alt="M-Kitchen" className="h-10 w-10 rounded-lg bg-black p-0.5 object-contain shrink-0" />
            <div className="min-w-0">
              <div className="font-black truncate">M-Kitchen</div>
              <div className="text-xs text-muted-foreground truncate" data-testid="branch-name">{menu?.sucursal?.nombre || "—"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mostrarSelectorHermanas && (
              <Select value={selectedSuc} onValueChange={onChangeSucursal}>
                <SelectTrigger className="h-9 w-44 hidden sm:flex" data-testid="branch-switch-select">
                  <SelectValue placeholder="Cambiar sucursal" />
                </SelectTrigger>
                <SelectContent>
                  {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="icon" onClick={toggle}><Moon className="h-4 w-4 dark:hidden" /><Sun className="h-4 w-4 hidden dark:inline" /></Button>
            <Button onClick={() => setCheckoutOpen(true)} disabled={items.length === 0} className="font-bold" data-testid="checkout-btn">
              <ShoppingCart className="h-4 w-4 mr-2" />{items.length} · {fmtMXN(total)}
            </Button>
          </div>
        </div>
        {mostrarSelectorHermanas && (
          <div className="sm:hidden border-t border-border bg-card/95 px-4 py-2">
            <Select value={selectedSuc} onValueChange={onChangeSucursal}>
              <SelectTrigger className="h-9 w-full" data-testid="branch-switch-select-mobile">
                <SelectValue placeholder="Cambiar sucursal" />
              </SelectTrigger>
              <SelectContent>
                {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Hambre <span className="ember-text">brasa</span>, pedido fácil.</h1>
          <p className="mt-2 text-muted-foreground">Selecciona tus productos y confirma tu pedido sin registro.</p>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          <Button size="sm" variant={activeCat === "ALL" ? "default" : "outline"} onClick={() => setActiveCat("ALL")}>Todos</Button>
          {menu?.categorias.map((c) => (
            <Button key={c.id} size="sm" variant={activeCat === c.id ? "default" : "outline"} onClick={() => setActiveCat(c.id)}>{c.nombre}</Button>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => {
            let precioMin = p.precio_base || 0;
            const tipoT = (p.variantes || []).find((g) => g.obligatoria);
            if (tipoT && tipoT.opciones?.length) {
              const minExtra = Math.min(...tipoT.opciones.map((o) => o.precio_extra || 0));
              precioMin = (p.precio_base || 0) + minExtra;
            }
            const hasObligatoria = (p.variantes || []).some((g) => g.obligatoria);
            return (
              <Card key={p.id} className="cursor-pointer hover:border-primary transition-colors" onClick={() => addProducto(p)} data-testid={`public-producto-${p.id}`}>
                <CardContent className="p-4">
                  <div className="aspect-video rounded-lg bg-secondary mb-3 overflow-hidden flex items-center justify-center">
                    {p.imagen_url ? <img src={p.imagen_url.startsWith("http") ? p.imagen_url : `${API}${p.imagen_url}`} alt={p.nombre} className="w-full h-full object-cover" /> : <Pizza className="h-12 w-12 text-muted-foreground" />}
                  </div>
                  <div className="font-bold">{p.nombre}</div>
                  {p.descripcion && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.descripcion}</div>}
                  {p.acompanamiento_nombre && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200" data-testid={`prod-acomp-badge-${p.id}`}>
                      🍟 Incluye {p.acompanamiento_nombre}
                      {p.acompanamiento_tiene_costo ? ` (+${fmtMXN(p.acompanamiento_costo || 0)})` : ""}
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-xl font-black text-primary">{hasObligatoria ? `desde ${fmtMXN(precioMin)}` : fmtMXN(precioMin)}</div>
                    <Plus className="h-5 w-5 text-primary" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Variantes */}
      <Dialog open={variantOpen} onOpenChange={setVariantOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{currentProduct?.nombre}</DialogTitle></DialogHeader>
          {currentProduct && (
            <div className="space-y-4">
              {(currentProduct.variantes || []).map((g) => (
                <div key={g.id}>
                  <div className="text-sm font-bold mb-2">{g.nombre}{g.obligatoria && <span className="text-destructive ml-1">*</span>}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {g.opciones.map((o) => {
                      const sel = currentVariantes.find((v) => v.grupo === g.nombre && v.opcion === o.nombre);
                      return (
                        <button key={o.id || o.nombre} type="button" onClick={() => setOpcion(g, o)}
                          className={`px-3 py-2 rounded-lg border-2 text-sm text-left transition-colors ${sel ? "border-primary bg-primary/10 font-bold" : "border-border hover:border-primary/50"}`}>
                          <div>{o.nombre}</div>
                          {o.precio_extra > 0 && <div className="text-xs text-muted-foreground">+{fmtMXN(o.precio_extra)}</div>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div><Label>Observaciones</Label><Textarea value={currentObs} onChange={(e) => setCurrentObs(e.target.value)} placeholder="Sin cebolla, etc." /></div>
              {/* Complemento configurado para el producto */}
              {currentProduct.acompanamiento_id && currentProduct.acompanamiento_nombre && (
                <div className="border-t border-border pt-3" data-testid="public-acomp-section">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Complemento</Label>
                  <label
                    className={`mt-2 flex items-center justify-between gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors ${incluirAcomp ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
                    data-testid="public-acomp-toggle"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={incluirAcomp}
                        onChange={(e) => setIncluirAcomp(e.target.checked)}
                        data-testid="public-acomp-checkbox"
                      />
                      <div className="min-w-0">
                        <div className="font-bold text-sm">🍟 {currentProduct.acompanamiento_nombre}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                          {currentProduct.acompanamiento_tiene_costo ? "Costo adicional" : "Incluido sin costo"}
                        </div>
                      </div>
                    </div>
                    <div className={`text-sm font-black ${currentProduct.acompanamiento_tiene_costo ? "text-primary" : "text-emerald-600 dark:text-emerald-400"}`}>
                      {currentProduct.acompanamiento_tiene_costo
                        ? `+${fmtMXN(currentProduct.acompanamiento_costo || 0)}`
                        : "Gratis"}
                    </div>
                  </label>
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label>Cantidad</Label>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="outline" onClick={() => setCurrentCantidad((c) => Math.max(1, c - 1))}><Minus className="h-4 w-4" /></Button>
                  <span className="text-2xl font-black w-10 text-center">{currentCantidad}</span>
                  <Button size="icon" variant="outline" onClick={() => setCurrentCantidad((c) => c + 1)}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setVariantOpen(false)}>Cancelar</Button><Button onClick={confirmVariantes}>Agregar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Confirmar pedido</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {items.map((it) => (
                <div key={it.id} className="border-b border-border pb-2 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{it.cantidad}× {it.nombre}</div>
                      {it.variantes.length > 0 && <div className="text-xs text-muted-foreground truncate">{it.variantes.map((v) => v.opcion).join(" · ")}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{fmtMXN(subtotalItem(it))}</span>
                      <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((x) => x.id !== it.id))}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  {it.acompanamiento_nombre && it.incluir_acompanamiento !== false && (
                    <div className="mt-1 ml-2 text-xs flex items-center gap-2 flex-wrap" data-testid={`cart-acomp-${it.id}`}>
                      <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-100">
                        🍟 PORCIÓN
                      </span>
                      <span className="font-semibold text-amber-700 dark:text-amber-300">{it.acompanamiento_nombre}</span>
                      {it.acompanamiento_tiene_costo ? (
                        <span className="text-primary font-bold">+{fmtMXN(it.acompanamiento_costo || 0)}</span>
                      ) : (
                        <span className="text-emerald-600 dark:text-emerald-400">(sin costo)</span>
                      )}
                    </div>
                  )}
                  {it.acompanamiento_nombre && it.incluir_acompanamiento === false && (
                    <div className="mt-1 ml-2 text-xs italic text-muted-foreground" data-testid={`cart-acomp-skipped-${it.id}`}>
                      Sin {it.acompanamiento_nombre}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between font-black text-xl"><span>Total</span><span className="text-primary">{fmtMXN(total)}</span></div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nombre *</Label><Input value={datos.cliente_nombre} onChange={(e) => setDatos({ ...datos, cliente_nombre: e.target.value })} data-testid="public-nombre-input" /></div>
              <div><Label>Teléfono *</Label><Input value={datos.cliente_telefono} onChange={(e) => setDatos({ ...datos, cliente_telefono: e.target.value })} data-testid="public-telefono-input" /></div>
            </div>

            <Tabs value={datos.entrega_tipo} onValueChange={(v) => setDatos({ ...datos, entrega_tipo: v })}>
              <TabsList className="grid grid-cols-2">
                <TabsTrigger value="RECOGE" data-testid="tab-recoge">Recoger en sucursal</TabsTrigger>
                <TabsTrigger value="DOMICILIO" data-testid="tab-domicilio">Entrega a domicilio</TabsTrigger>
              </TabsList>
              <TabsContent value="DOMICILIO" className="space-y-3 mt-3">
                <div><Label>Dirección</Label><Textarea value={datos.cliente_direccion} onChange={(e) => setDatos({ ...datos, cliente_direccion: e.target.value, direccion_origen: "MANUAL" })} placeholder="Calle, número, colonia…" /></div>
                <div><Label>Referencias</Label><Input value={datos.referencias} onChange={(e) => setDatos({ ...datos, referencias: e.target.value })} /></div>
                <Button variant="outline" className="w-full" onClick={usarGps} data-testid="public-gps-btn"><MapPin className="h-4 w-4 mr-1" />Compartir mi ubicación actual</Button>
                {datos.latitud && datos.longitud && (
                  <div className="space-y-2">
                    <MapPreview lat={datos.latitud} lng={datos.longitud} onChange={onMoveMarker} height={220} />
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Marcador arrastrable — ajusta si la ubicación no es exacta.
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Método de pago */}
            <div className="space-y-2 pt-2 border-t border-border">
              <Label>Método de pago *</Label>
              <div className="grid grid-cols-3 gap-2" data-testid="metodo-pago-grid">
                {[
                  { v: "EFECTIVO", label: "Efectivo", icon: Banknote },
                  { v: "TRANSFERENCIA", label: "Transferencia", icon: ArrowLeftRight },
                  { v: "TARJETA", label: "Tarjeta", icon: CreditCard },
                ].map(({ v, label, icon: Icon }) => {
                  const active = datos.metodo_pago === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setDatos({ ...datos, metodo_pago: v, monto_con_que_paga: v === "EFECTIVO" ? datos.monto_con_que_paga : "" })}
                      className={`flex flex-col items-center gap-1 rounded-lg border-2 px-2 py-3 text-xs font-bold transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}
                      data-testid={`metodo-pago-${v.toLowerCase()}`}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>

              {requiereMontoConCualPaga && (
                <div className="space-y-1 mt-2 rounded-lg bg-secondary/40 p-3 border border-border" data-testid="monto-paga-container">
                  <Label>¿Con cuánto va a pagar? *</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min={total}
                    step="0.01"
                    placeholder={fmtMXN(total)}
                    value={datos.monto_con_que_paga}
                    onChange={(e) => setDatos({ ...datos, monto_con_que_paga: e.target.value })}
                    data-testid="monto-paga-input"
                  />
                  <div className="text-xs text-muted-foreground">
                    El repartidor llevará el cambio. Mínimo: <span className="font-bold">{fmtMXN(total)}</span>
                  </div>
                  {cambioCalculado !== null && (
                    <div className="text-sm" data-testid="cambio-preview">
                      Cambio: <span className="font-black text-emerald-600 dark:text-emerald-400">{fmtMXN(cambioCalculado)}</span>
                    </div>
                  )}
                  {datos.monto_con_que_paga && montoPaga < total && (
                    <div className="text-xs text-destructive">El monto debe ser mayor o igual al total</div>
                  )}
                </div>
              )}

              {datos.metodo_pago === "TRANSFERENCIA" && (
                <div className="text-xs text-muted-foreground rounded-md bg-secondary/40 p-2 border border-border">
                  Al confirmar, la sucursal te contactará al teléfono indicado con los datos para realizar la transferencia.
                </div>
              )}
              {datos.metodo_pago === "TARJETA" && (
                <div className="text-xs text-muted-foreground rounded-md bg-secondary/40 p-2 border border-border">
                  {datos.entrega_tipo === "DOMICILIO"
                    ? "El repartidor llevará terminal para cobrar al momento de la entrega."
                    : "Pagarás con tarjeta al recoger en sucursal."}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutOpen(false)}>Cancelar</Button>
            <Button onClick={enviar} disabled={submitting} className="font-bold" data-testid="public-confirm-btn">{submitting ? "Enviando…" : `Confirmar ${fmtMXN(total)}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
